import { apiGet, apiPost, ApiError, handleApiError } from './apiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { GroupageTrip, GroupageTripStatus, GroupageTripWithNegotiations } from '../models/groupageTrip';
import { t } from 'i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export const listGroupageTrips = async (vehicleId: number, status?: GroupageTripStatus, afterBookingId?: number | null): Promise<GroupageTrip[]> => {
  try {
    const query: Record<string, string> = {};
    if(status !== undefined && status !== null) query.status = String(status);
    if(afterBookingId !== undefined && afterBookingId !== null) query.after_booking_id = String(afterBookingId);
    const response = await apiGet(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.list(vehicleId)}`, query);
    const data = await response.json();
    return data.result || [];
  } catch(error) {
    handleApiError(error, t('Failed to fetch groupage trips'));
    throw error;
  }
};

const NON_TERMINAL_STATUSES = new Set([GroupageTripStatus.ACTIVE, GroupageTripStatus.FULL, GroupageTripStatus.IN_PROGRESS]);

export const getActiveGroupageTrip = async (vehicleId: number, afterBookingId?: number | null): Promise<GroupageTrip | null> => {
  const trips = await listGroupageTrips(vehicleId, undefined, afterBookingId);
  return trips.find(t => NON_TERMINAL_STATUSES.has(t.status)) ?? null;
};

export const getGroupageTrip = async (vehicleId: number, tripId: number): Promise<GroupageTripWithNegotiations> => {
  try {
    const response = await apiGet(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.get(vehicleId, tripId)}`);
    const data = await response.json();
    const {trip, negotiations} = data.result;
    return {...trip, negotiations};
  } catch(error) {
    handleApiError(error, t('Failed to fetch groupage trip'));
    throw error;
  }
};

export const createGroupageTrip = async (vehicleId: number, negotiationIds: string[], afterBookingId?: number | null): Promise<number> => {
  try {
    const body: Record<string, unknown> = {negotiation_ids: negotiationIds};
    if(afterBookingId !== undefined && afterBookingId !== null) body.after_booking_id = afterBookingId;
    const response = await apiPost(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.create(vehicleId)}`, body);
    const data = await response.json();
    return data.result;
  } catch(error) {
    // Idempotency: backend returns 409 with the existing trip id on concurrent create or duplicate click.
    // Surface it as success so the UI opens the existing trip instead of erroring.
    if(error instanceof ApiError && error.status === 409 && error.errorData?.error?.errorKey === 'groupage.alreadyActive') {
      const info = error.errorData.error.additionalInfo as {tripId?: number} | undefined;
      if(info?.tripId) return info.tripId;
      // Backend didn't include tripId — look it up ourselves so the UI still opens the winning trip.
      const fallback = await getActiveGroupageTrip(vehicleId, afterBookingId);
      if(fallback) return fallback.id;
    }
    handleApiError(error, t('Failed to create groupage trip'));
    throw error;
  }
};

export const addNegotiationToTrip = async (vehicleId: number, tripId: number, negotiationId: string): Promise<GroupageTrip> => {
  try {
    const response = await apiPost(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.addNegotiation(vehicleId, tripId)}`, {negotiation_id: negotiationId});
    const data = await response.json();
    return data.result;
  } catch(error) {
    handleApiError(error, t('Failed to add negotiation to groupage trip'));
    throw error;
  }
};

export const removeNegotiationFromTrip = async (vehicleId: number, tripId: number, negotiationId: string): Promise<GroupageTrip> => {
  try {
    const response = await apiPost(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.removeNegotiation(vehicleId, tripId)}`, {negotiation_id: negotiationId});
    const data = await response.json();
    return data.result;
  } catch(error) {
    handleApiError(error, t('Failed to remove negotiation from groupage trip'));
    throw error;
  }
};

export const reorderGroupageTripNegotiations = async (vehicleId: number, tripId: number, negotiationIds: string[]): Promise<GroupageTripWithNegotiations> => {
  try {
    const response = await apiPost(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.reorder(vehicleId, tripId)}`, {negotiation_ids: negotiationIds});
    const data = await response.json();
    const {trip, negotiations} = data.result;
    return {...trip, negotiations};
  } catch(error) {
    handleApiError(error, t('Failed to reorder groupage trip'));
    throw error;
  }
};

export const updateGroupageTripStatus = async (vehicleId: number, tripId: number, status: GroupageTripStatus): Promise<GroupageTrip> => {
  try {
    const response = await apiPost(`${API_BASE_URL}${API_ENDPOINTS.vehicle.groupageTrip.updateStatus(vehicleId, tripId)}`, {status});
    const data = await response.json();
    return data.result;
  } catch(error) {
    handleApiError(error, t('Failed to update groupage trip status'));
    throw error;
  }
};
